import * as React from 'react';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useCreatePromotionCode,
  useUpdatePromotionCode,
  type PromotionCodeItem,
  type CreatePromotionCodeInput,
  type UpdatePromotionCodeInput,
} from '@/lib/api/promotionCodes';
import {
  EditablePromotionCodeRow,
  type PromotionCodeFormData,
} from './editable-promotion-code-row';
import { DeletePromotionCodeModal } from './delete-promotion-code-modal';
import { BulkDeletePromotionCodesModal } from './bulk-delete-promotion-codes-modal';

interface EditablePromotionCodesTableProps {
  promotionCodes: PromotionCodeItem[];
  promotionId: string;
  storeId: string;
}

let nextTempId = 0;
const createTempId = () => {
  nextTempId += 1;
  return `__new_${nextTempId}`;
};

export const EditablePromotionCodesTable = ({
  promotionCodes,
  promotionId,
  storeId,
}: EditablePromotionCodesTableProps) => {
  const { t } = useTranslation('stores');

  const [editingIds, setEditingIds] = React.useState<Set<string>>(new Set());
  const [newRowIds, setNewRowIds] = React.useState<string[]>([]);
  const [savingIds, setSavingIds] = React.useState<Set<string>>(new Set());
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set());
  const [deletingCodeId, setDeletingCodeId] = React.useState<string | undefined>();
  const [showBulkDelete, setShowBulkDelete] = React.useState(false);

  const createMutation = useCreatePromotionCode();
  const updateMutation = useUpdatePromotionCode();

  const allRowIds = [...promotionCodes.map((c) => c.id), ...newRowIds];
  const allSelected = allRowIds.length > 0 && allRowIds.every((id) => selectedIds.has(id));
  const someSelected = allRowIds.some((id) => selectedIds.has(id));

  const toggleSelectAll = (checked: boolean) => {
    setSelectedIds(checked ? new Set(allRowIds) : new Set());
  };

  const toggleSelect = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const startEditing = (id: string) => {
    setEditingIds((prev) => new Set(prev).add(id));
  };

  const stopEditing = (id: string) => {
    setEditingIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const insertRow = () => {
    const tempId = createTempId();
    setNewRowIds((prev) => [...prev, tempId]);
    setEditingIds((prev) => new Set(prev).add(tempId));
  };

  const removeNewRow = (tempId: string) => {
    setNewRowIds((prev) => prev.filter((id) => id !== tempId));
    stopEditing(tempId);
    setSelectedIds((prev) => {
      if (!prev.has(tempId)) return prev;
      const next = new Set(prev);
      next.delete(tempId);
      return next;
    });
  };

  const handleSaveExisting = async (codeId: string, data: PromotionCodeFormData) => {
    setSavingIds((prev) => new Set(prev).add(codeId));
    try {
      const input: UpdatePromotionCodeInput = {
        id: codeId,
        data: {
          code: data.code,
          active: data.active,
          usageLimit: data.usageLimit,
          expiresAt: data.expiresAt,
          emailPattern: data.emailPattern,
          firstTimeCustomersOnly: data.firstTimeCustomersOnly,
        },
      };
      await updateMutation.mutateAsync(input);
      stopEditing(codeId);
    } finally {
      setSavingIds((prev) => {
        const next = new Set(prev);
        next.delete(codeId);
        return next;
      });
    }
  };

  const handleSaveNew = async (tempId: string, data: PromotionCodeFormData) => {
    setSavingIds((prev) => new Set(prev).add(tempId));
    try {
      const input: CreatePromotionCodeInput = {
        code: data.code,
        promotionId,
        usageLimit: data.usageLimit,
        expiresAt: data.expiresAt,
        emailPattern: data.emailPattern,
        firstTimeCustomersOnly: data.firstTimeCustomersOnly,
      };
      await createMutation.mutateAsync(input);
      removeNewRow(tempId);
    } finally {
      setSavingIds((prev) => {
        const next = new Set(prev);
        next.delete(tempId);
        return next;
      });
    }
  };

  const handleDeleteSuccess = () => {
    setSelectedIds(new Set());
  };

  const selectedCount = selectedIds.size;
  const persistedSelectedIds = Array.from(selectedIds).filter((id) => !id.startsWith('__new_'));

  return (
    <div className="space-y-2">
      {selectedCount > 0 && (
        <div className="flex items-center gap-2 rounded-md border bg-muted/50 px-3 py-2">
          <span className="text-sm text-muted-foreground">
            {t('promotionCodes.selectedRow', { count: selectedCount })}
          </span>
          <Button
            variant="destructive"
            size="sm"
            disabled={persistedSelectedIds.length === 0}
            onClick={() => setShowBulkDelete(true)}
          >
            {t('promotionCodes.deleteSelected')}
          </Button>
        </div>
      )}

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox
                  checked={allSelected || (someSelected && 'indeterminate')}
                  onCheckedChange={(checked) => toggleSelectAll(!!checked)}
                />
              </TableHead>
              <TableHead>
                {t('promotionCodes.columns.code')}
              </TableHead>
              <TableHead className="w-24">
                {t('promotionCodes.columns.status')}
              </TableHead>
              <TableHead className="w-28">
                {t('promotionCodes.columns.usage')}
              </TableHead>
              <TableHead className="w-32">
                {t('promotionCodes.columns.revenue')}
              </TableHead>
              <TableHead className="w-44">
                {t('promotionCodes.columns.expires')}
              </TableHead>
              <TableHead>
                {t('promotionCodes.columns.email')}
              </TableHead>
              <TableHead className="w-32">
                {t('promotionCodes.columns.firstTimeOnly')}
              </TableHead>
              <TableHead className="w-24">
                {t('promotionCodes.columns.actions')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {promotionCodes.length === 0 && newRowIds.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} className="h-24 text-center text-muted-foreground">
                  {t('promotionCodes.empty')}
                </TableCell>
              </TableRow>
            )}

            {promotionCodes.map((promotionCode) => (
              <EditablePromotionCodeRow
                key={promotionCode.id}
                promotionCode={promotionCode}
                isEditing={editingIds.has(promotionCode.id)}
                isSaving={savingIds.has(promotionCode.id)}
                selected={selectedIds.has(promotionCode.id)}
                onSelectChange={(checked) => toggleSelect(promotionCode.id, checked)}
                onEdit={() => startEditing(promotionCode.id)}
                onCancel={() => stopEditing(promotionCode.id)}
                onSave={(data) => handleSaveExisting(promotionCode.id, data)}
                onDelete={() => setDeletingCodeId(promotionCode.id)}
              />
            ))}

            {newRowIds.map((tempId) => (
              <EditablePromotionCodeRow
                key={tempId}
                isEditing
                isSaving={savingIds.has(tempId)}
                selected={selectedIds.has(tempId)}
                onSelectChange={(checked) => toggleSelect(tempId, checked)}
                onEdit={() => {}}
                onCancel={() => removeNewRow(tempId)}
                onSave={(data) => handleSaveNew(tempId, data)}
                onDelete={() => removeNewRow(tempId)}
              />
            ))}
          </TableBody>
        </Table>
      </div>

      <Button variant="outline" size="sm" onClick={insertRow}>
        <Plus className="h-4 w-4" />
        {t('promotionCodes.addRow')}
      </Button>

      <DeletePromotionCodeModal
        codeId={deletingCodeId}
        onClose={() => setDeletingCodeId(undefined)}
        onSuccess={handleDeleteSuccess}
      />

      {showBulkDelete && (
        <BulkDeletePromotionCodesModal
          codeIds={persistedSelectedIds}
          onClose={() => setShowBulkDelete(false)}
          onSuccess={handleDeleteSuccess}
        />
      )}
    </div>
  );
};
